import { Alpha3Code } from 'i18n-iso-countries'
import { AxiosInstance } from 'axios'

import { Organisation } from '../nonprofits/nonprofitsService.types'
import { PBPListResponse } from '../types/pbpResponses.types'

import { TableColumnSortDirection } from '@percent/lemonade'
import { CurrencyCode, Money } from '@percent/utility'

export type WorkplaceGivingServiceCapabilities = {
  percentClient: AxiosInstance
}

export type WorkplaceParamsProps = {
  page?: number | null
  pageSize?: number | null
}

export type GetEmployeesListProps = WorkplaceParamsProps & {
  filter?: string | null
}

type MatchingBudget = Record<'remaining' | 'total' | 'used', Money>

export type Employee = {
  email: string
  fullName: string | null
  id: string
  matching: MatchingBudget | null
  team: string[]
  wallet: {
    balance: Money
  } | null
  currency: CurrencyCode | null
  country: string | null
  volunteeringRewardUsage?: VolunteeringRewardUsage | null
}

export type VolunteeringRewardUsage = {
  limit: Money
  earned: Money
  remaining: Money
}

export type EmployeesListResponse = PBPListResponse<Employee> & {
  volunteeringRewardBudgetEnabled?: boolean
}

export type UpdatedEmployee = Omit<Employee, 'currency'> & {
  currency: CurrencyCode
}

export type AddWalletBalanceProps = {
  accountId: string
  amount: Money
  fundingSourceId?: string
  expiresAt?: string
}

export type AssignAchievementProps = {
  accountId: string
  achievementId: string
}

export type UpdateMatchingBudgetProps = Pick<AddWalletBalanceProps, 'accountId'> & { total: Money }

export type DonationWallet = {
  id: string
  partnerId: string
  accountId: string
  balance: Money
}

export type ConditionStateAndConditionAccountLimit = Omit<DonationWallet, 'balance'> & {
  createdAt: string
  conditionId: string
  state: MatchingBudget
  change: {
    id: string
    change: string
    createdAt: string
    createdBy: string
    oldState: MatchingBudget
    newState: MatchingBudget
    kind: 'account_limit'
  }
}

export type FeaturedItemSummary = {
  kind: 'opportunity' | 'fundraiser'
  itemId: string
  countries: string[]
}

export type VolunteeringOpportunitySortField = 'name' | 'attendees' | 'featuredAt' | 'startDate'

export enum OpportunityVisibility {
  LISTED = 'listed',
  UNLISTED = 'unlisted'
}

export type GetVolunteeringOpportunitiesListProps = {
  page?: number | null
  pageSize?: number | null
  query?: string | null
  parentId?: string | null
  status?: (string | null)[] | null
  availableOnly?: boolean
  visibility?: OpportunityVisibility[]
  sort?: VolunteeringOpportunitySortField | null
  direction?: TableColumnSortDirection | null
}

export enum OpportunityStatus {
  DRAFT = 'draft',
  ACTIVE = 'published',
  CLOSED = 'closed',
  CANCELLED = 'cancelled'
}

export enum OpportunityType {
  PROJECT = 'project',
  EVENT = 'event',
  EVENT_SERIES = 'event_series',
  EVENT_SERIES_SESSION = 'event_series_session'
}

export enum OpportunityLocationType {
  VIRTUAL = 'virtual',
  OFFLINE = 'in-person'
}

export type OnlineLocation = {
  type: OpportunityLocationType.VIRTUAL
  link?: string
}

export type OfflineLocation = {
  type: OpportunityLocationType.OFFLINE
  country: string
  city: string
  zipCode: string
  addressLineOne: string
  addressLineTwo?: string
  locationName?: string
  locationDisplayName?: string
  long: number
  lat: number
}

export type OpportunityLocationDetails = OnlineLocation | OfflineLocation

export type OpportunityTimeTracking = 'automatic' | 'manual'

export type VolunteeringOpportunity = {
  id: string
  name: string
  description: string | null
  image: string | null
  skills: {
    id: string
    name: string
  }[]
  itinerary:
    | {
        id: number
        durationMinutes: number
        title: number
        description?: string
      }[]
    | null
  location: OpportunityLocationDetails
  startDate: string | null
  endDate: string | null
  parentId: string | null
  activities: {
    id: string
    name: string
  }[]
  external: {
    provider: string
  } | null
  organisation: (Pick<Organisation, 'id'> & Partial<Pick<Organisation, 'name' | 'logo' | 'countryCode'>>) | null
  managedByOrganisation?: boolean
  participants: {
    spots: number | null
    count: number
  }
  participantCounts?: {
    registered: number
    confirmed: number
    waitlisted: number
    spots: number | null
  }
  timeTracking: OpportunityTimeTracking
  organiser: {
    id: string
    email: string | null
    fullName: string | null
  } | null
  type: OpportunityType
  status: OpportunityStatus
  createdAt: string
  publishedAt?: string | null
  featured?: {
    featuredItemId: string
    country: Alpha3Code[]
  }
  surveyIds?: string[]
  visibility?: OpportunityVisibility
}

export type VolunteeringOpportunityDetails = Omit<VolunteeringOpportunity, 'createdBy'> & {
  organiser: {
    id: string
    email: string | null
    fullName: string | null
  } | null
}

export type OpportunityParticipant = {
  id: string
  accountId: string
  fullName: string
  image: string | null
  email?: string | null
  notificationEmails?: string[]
}

export type CancelOpportunityErrorCode =
  | 'opportunity/cannot_be_cancelled'
  | 'opportunity/cancel_invalid_state'
  | 'opportunity/cannot_have_published_children'
  | 'opportunity/external'

export type CloseOpportunityErrorCode =
  | 'opportunity/close_invalid_state'
  | 'opportunity/cannot_close_future'
  | 'opportunity/external'

export type PublishOpportunityErrorCode =
  | 'opportunity/publish_invalid_state'
  | 'opportunity/publish_invalid_dates'
  | 'opportunity/external'

export type UnpublishOpportunityErrorCode =
  | 'opportunity/unpublish_invalid_state'
  | 'opportunity/unpublish_has_participants'
  | 'opportunity/unpublish_has_sessions'
  | 'opportunity/external'

export type Achievement = {
  id: string
  achievementId: string
  name: string
  image: string
}

export enum Kind {
  Fundraiser = 'fundraiser',
  Opportunity = 'opportunity'
}

export type FeatureItemProps = {
  kind: Kind
  itemId: string
  country: string[]
}

export type FeatureItemResponse = {
  id: string
  createdAt: string
  updatedAt: string
  createdBy: string
  partnerId: string
  kind: Kind
  itemId: string
  country: string[]
}

export enum SlackInstallationStatus {
  INSTALLED = 'installed',
  NOT_INSTALLED = 'not_installed'
}

export type GetSlackIntegrationStatusResponse = {
  status: SlackInstallationStatus
  id: string | null
  teamName: string | null
  createdAt: string | null
}

export enum GivingCampaignStatusDto {
  DRAFT = 'DRAFT',
  PUBLISHED = 'PUBLISHED',
  CANCELLED = 'CANCELLED'
}

export type GivingCampaignPartnerDto = Readonly<{
  id: string
  title: string
  startDate: string | null
  endDate: string | null
  createdAt: string
  status: GivingCampaignStatusDto
  groups: { id: string; name: string }[]
}>

export type GetGivingCampaignsListProps = Readonly<{
  page?: number | null
  pageSize?: number | null
}>

export type GivingCampaignDto = GivingCampaignPartnerDto &
  Readonly<{
    shortDescription: string
    description: string
    coverImage: string
  }>

export type GivingCampaignOverviewDto = Readonly<{
  id: string
  title: string
  shortDescription: string
  coverImage: string
}>

export type GivingCampaignGroupPartnerDto = Readonly<{
  id: string
  name: string
  campaignsCount: number
  deletedAt: string | null
}>

export type VolunteeringTimeLogType = 'on_platform' | 'off_platform'

export type VolunteeringTimeLog = {
  id: string
  employee: string
  opportunity: string | null
  opportunityId: string | null
  startTime: string
  endTime: string
  type: VolunteeringTimeLogType
  rewards?: VolunteeringTimeLogReward[]
}

export type VolunteeringTimeLogReward = {
  accountRewardId: string
  organisationId: string
  contributedAmount: Money
  rewardAmount: Money
}

export type TimeLogSearchField = 'opportunity' | 'employee'

export type GetVolunteeringTimeLogsListProps = {
  pageSize?: number | null
  opportunityId?: string | null
  accountId?: string | null
  status?: string[] | null
  dateFrom?: string | null
  dateTo?: string | null
  type?: VolunteeringTimeLogType | null
  expand?: 'rewards' | null
}

export type VolunteeringActivity = {
  id: string
  name: string
}

export type GetVolunteeringActivitiesListProps = {
  pageSize?: number | null
  sort?: 'name' | 'createdAt' | null
  direction?: 'ASC' | 'DESC' | null
}

export type CreateVolunteeringActivityProps = {
  name: string
}

export type EditVolunteeringActivityProps = {
  id: string
  name: string
}

type EmployeeAchievementCondition = {
  factKind?: string
  operator: string
  description?: string
  descriptionLines?: string[]
  n?: number
  value?: number | string | string[]
  currentValue?: number | string | string[]
  pass?: boolean
  conditions?: EmployeeAchievementCondition[]
}

export type EmployeeAchievementsResponse = {
  rulesAndAchievements: {
    accountAchievement: null | {
      id: string
      assignedBy: string | null
      createdAt: string
    }
    achievement: {
      id: string
      image: string
      name: string
    }
    rule: {
      id: string
      conditions: EmployeeAchievementCondition
    }
  }[]
}
